import React, { useState, useRef, useEffect } from 'react';

import { useAppDispatch, useAppSelector } from 'src/hooks/redux';
import { searchPeopleSelector } from 'src/components/ui/form/peoplePicker/selectors';
import { searchPeople } from 'src/components/ui/form/peoplePicker/actions';
import { selectUsers } from 'src/modules/users/selector';
import Loading from 'src/components/Loading';
import { UserListItem } from 'src/pages/DashboardPage/vpo/types/typesVPO';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface UserData {
  avatar: string | null;
  shortName: string;
  displayName: string;
  email: string;
}

interface UsersState {
  [email: string]: UserData;
}

type SearchPeopleItem = {
  id?: number;
  value: string;
  label: string;
  avatar: string | null;
};

interface UserSelectProps {
  selectedUsers: any[];
  onUserFilterChange: (users: UserListItem[]) => void;
  initialSearchValue?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
}

export const UserSelect = ({ selectedUsers, onUserFilterChange, initialSearchValue, label, required = false, disabled = false }: UserSelectProps) => {
  const [searchString, setSearchString] = useState(initialSearchValue || '');
  const [isFocused, setIsFocused] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const dispatch = useAppDispatch();
  const users: UsersState = useAppSelector(selectUsers);
  const people: SearchPeopleItem[] = useAppSelector(searchPeopleSelector);
  const status = useAppSelector((state) => state.searchUsers.status);

  useEffect(() => {
    if (searchString && searchString.length >= 3) {
      dispatch(searchPeople(searchString));
    }
  }, [searchString, dispatch]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleUserSelect = (person: SearchPeopleItem) => {
    if (disabled) return;
    const newUser: UserListItem = {
      id: person.id,
      email: person.value,
      title: person.label,
      avatar: person.avatar,
      shortName: person.label,
    };

    const isSelected = selectedUsers.some((u) => u.email === person.value);

    if (isSelected) {
      onUserFilterChange(selectedUsers.filter((u) => u.email !== person.value));
    } else {
      onUserFilterChange([...selectedUsers, newUser]);
      setSearchString('');
    }
  };

  const handleRemoveUser = (email: string) => {
    if (disabled) return;
    onUserFilterChange(selectedUsers.filter((user) => user.email !== email));
  };

  const renderUserAvatar = (avatar: string | null, shortName: string, displayName: string) => {
    if (avatar) {
      return <img src={avatar} alt={displayName} className='w-8 h-8 rounded-full object-cover' />;
    }
    return <div className='w-8 h-8 rounded-full bg-[#012169] flex items-center justify-center text-sm font-medium text-white'>{shortName}</div>;
  };

  const showDropdown = isFocused && !disabled && (searchString?.length || 0) >= 3;

  return (
    <div className='flex flex-col gap-1'>
      <label className='text-black text-base flex justify-between items-center'>
        {label}
        {required && <span className='text-[#BA0C2F]'>*</span>}
      </label>

      <div className='relative' ref={containerRef}>
        <div
          className={`w-full text-base rounded-md border px-5 py-1.5 outline-none shadow-custom min-h-[52px] flex items-center gap-2 flex-wrap ${
            disabled
              ? 'bg-[#E5E7EB] text-[#6B7280] border-[#E5E7EB] cursor-not-allowed'
              : 'bg-white text-black border-[#DFE4EA] cursor-text'
          }`}
          onClick={() => !disabled && inputRef.current?.focus()}
        >
          {selectedUsers.map((user) => (
            <div key={user.email} className='flex items-center bg-gray-100 rounded-md pr-2 pl-1 h-10'>
              {renderUserAvatar(users[user.email]?.avatar, users[user.email]?.shortName, users[user.email]?.displayName)}
              <span className='text-sm ml-3'>{user.title}</span>
              {!disabled && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveUser(user.email);
                  }}
                  className='ml-1 text-gray-400 hover:text-gray-600'
                >
                  <XMarkIcon className='w-6 h-6' aria-hidden='true' />
                </button>
              )}
            </div>
          ))}
          {!disabled && (
            <input
              ref={inputRef}
              type='text'
              value={searchString}
              placeholder={selectedUsers.length > 0 ? '' : 'Найти сотрудников'}
              onFocus={() => setIsFocused(true)}
              onChange={(e) => setSearchString(e.target.value)}
              className='flex-1 min-w-[120px] bg-transparent border-none shadow-none p-0 outline-none focus:ring-0 text-base placeholder:text-gray-400'
            />
          )}
          {disabled && selectedUsers.length === 0 && (
            <span className='text-sm text-[#6B7280]'>Найти сотрудников</span>
          )}
        </div>

        {showDropdown && (
          <div className='absolute z-[101] top-full left-0 right-0 mt-1 bg-white border border-[#DFE4EA] rounded-md shadow-lg max-h-60 overflow-y-auto'>
            {status === 'LOADING' ? (
              <div className='flex items-center justify-center p-4'>
                <Loading />
              </div>
            ) : (
              <div className='p-2'>
                {people.map((person) => {
                  const isSelected = selectedUsers.some((u) => u.email === person.value);
                  return (
                    <div key={person.value} className={`flex items-center gap-3 p-2 hover:bg-gray-50 cursor-pointer ${isSelected ? 'bg-gray-50' : ''}`} onClick={() => handleUserSelect(person)}>
                      {renderUserAvatar(users[person.value]?.avatar, users[person.value]?.shortName, users[person.value]?.displayName)}
                      <span className='text-sm'>{person.label}</span>
                    </div>
                  );
                })}
                {status === 'SUCCESS' && people.length === 0 && (
                  <div className='p-4 text-center text-gray-500'>
                    Нет совпадений по запросу <strong>{searchString}</strong>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
